import * as R from 'ramda'
import {OrderPaid} from '../Entities/OrderPaid'
import {filterMap} from './ArrayHelper'
import * as Dictionary from './Dictionary'
import * as OrderStatus from '../Entities/OrderStatus'
import {ShippingMethod} from '../Entities/ShippingMethod'
import {LocalDateTime, LocalTime} from 'js-joda'
import {OrderFilter} from '../Components/OrderFiltering/Model/OrderFilter'

export type SerializedOrderFilter =
    | StatusFilter
    | CustomerIdFilter
    | BpsUserIdFilter
    | CompanyIdFilter
    | OrderNumberFilter
    | ShippingMethodFilter
    | CreatedFromFilter
    | CreatedToFilter
    | PriceFromFilter
    | PriceToFilter
    | OrderPaidFilter

interface StatusFilter {
    readonly type: 'StatusFilter'
    readonly status: OrderStatus.M
}

interface CustomerIdFilter {
    readonly type: 'CustomerIdFilter'
    readonly customerId: number
}

interface BpsUserIdFilter {
    readonly type: 'BpsUserIdFilter'
    readonly bpsUserId: number
}

interface CompanyIdFilter {
    readonly type: 'CompanyIdFilter'
    readonly companyId: number
}

interface OrderNumberFilter {
    readonly type: 'OrderNumberFilter'
    readonly orderNumber: string
}

interface ShippingMethodFilter {
    readonly type: 'ShippingMethodFilter'
    readonly shippingMethod: ShippingMethod
}

interface CreatedFromFilter {
    readonly type: 'CreatedFromFilter'
    readonly createdFrom: LocalDateTime
}

interface CreatedToFilter {
    readonly type: 'CreatedToFilter'
    readonly createdTo: LocalDateTime
}

interface PriceFromFilter {
    readonly type: 'PriceFromFilter'
    readonly priceFrom: number
}

interface PriceToFilter {
    readonly type: 'PriceToFilter'
    readonly priceTo: number
}

interface OrderPaidFilter {
    readonly type: 'OrderPaidFilter'
    readonly orderPaid: string
}

export function fromOrderFilters(
    orderFilters: OrderFilter[],
): SerializedOrderFilter[] {
    return R.map(
        (orderFilter): SerializedOrderFilter => {
            switch (orderFilter.type) {
                case 'StatusFilter':
                    return orderFilter
                case 'CustomerFilter':
                    return {
                        type: 'CustomerIdFilter',
                        customerId: orderFilter.customer.id,
                    }
                case 'BpsUserFilter':
                    return {
                        type: 'BpsUserIdFilter',
                        bpsUserId: orderFilter.bpsUser.id,
                    }
                case 'CompanyFilter':
                    return {
                        type: 'CompanyIdFilter',
                        companyId: orderFilter.company.id,
                    }
                case 'OrderNumberFilter':
                    return orderFilter
                case 'ShippingMethodFilter':
                    return orderFilter
                case 'CreatedFromFilter':
                    return {
                        type: 'CreatedFromFilter',
                        createdFrom: LocalDateTime.of(
                            orderFilter.createdFrom,
                            LocalTime.of(0, 0, 0, 0),
                        ),
                    }
                case 'CreatedToFilter':
                    return {
                        type: 'CreatedToFilter',
                        createdTo: LocalDateTime.of(
                            orderFilter.createdTo,
                            LocalTime.of(0, 0, 0, 0),
                        ),
                    }
                case 'PriceFromFilter':
                    return orderFilter
                case 'PriceToFilter':
                    return orderFilter
                case 'OrderPaidFilter':
                    return orderFilter
            }
        },
        orderFilters,
    )
}

export function toQueryParameters(
    filters: SerializedOrderFilter[]
): Dictionary.Dictionary<string> {
    return Dictionary.fromList(
        R.map(
            (filter): [string, string] => {
                switch (filter.type) {
                    case 'StatusFilter':
                        return ['status', filter.status]
                    case 'CustomerIdFilter':
                        return ['customerId', filter.customerId.toString()]
                    case 'BpsUserIdFilter':
                        return ['bpsUserId', filter.bpsUserId.toString()]
                    case 'CompanyIdFilter':
                        return ['companyId', filter.companyId.toString()]
                    case 'OrderNumberFilter':
                        return ['orderNumber', filter.orderNumber]
                    case 'ShippingMethodFilter':
                        return ['shippingMethod', filter.shippingMethod]
                    case 'CreatedFromFilter':
                        return ['createdFrom', filter.createdFrom.toString()]
                    case 'CreatedToFilter':
                        return ['createdTo', filter.createdTo.toString()]
                    case 'PriceFromFilter':
                        return ['priceFrom', filter.priceFrom.toString()]
                    case 'PriceToFilter':
                        return ['priceTo', filter.priceTo.toString()]
                    case 'OrderPaidFilter':
                        return ['orderPaid', filter.orderPaid]
                }
            },
            filters,
        )
    )
}

export function fromQueryParameters(
    queryParameters: Dictionary.Dictionary<string>
): SerializedOrderFilter[] {
    // TODO return Result, handle wrong urls
    return filterMap(
        ([key, value]: [string, string]): SerializedOrderFilter | null => {
            switch (key) {
                case 'status':
                    return {
                        type: 'StatusFilter',
                        // tslint:disable-next-line no-any
                        status: (value as any) as OrderStatus.M,
                    }
                case 'customerId':
                    return {
                        type: 'CustomerIdFilter',
                        customerId: parseInt(value, 10),
                    }
                case 'bpsUserId':
                    return {
                        type: 'BpsUserIdFilter',
                        bpsUserId: parseInt(value, 10),
                    }
                case 'companyId':
                    return {
                        type: 'CompanyIdFilter',
                        companyId: parseInt(value, 10),
                    }
                case 'orderNumber':
                    return {
                        type: 'OrderNumberFilter',
                        orderNumber: value,
                    }
                case 'shippingMethod':
                    return {
                        type: 'ShippingMethodFilter',
                        // tslint:disable-next-line no-any
                        shippingMethod: (value as any) as ShippingMethod,
                    }
                case 'createdFrom':
                    return {
                        type: 'CreatedFromFilter',
                        createdFrom: LocalDateTime.parse(value),
                    }
                case 'createdTo':
                    return {
                        type: 'CreatedToFilter',
                        createdTo: LocalDateTime.parse(value),
                    }
                case 'priceFrom':
                    return {
                        type: 'PriceFromFilter',
                        priceFrom: parseFloat(value),
                    }
                case 'priceTo':
                    return {
                        type: 'PriceToFilter',
                        priceTo: parseFloat(value),
                    }
                case 'orderPaid':
                    return {
                        type: 'OrderPaidFilter',
                        // tslint:disable-next-line no-any
                        orderPaid: (value as any) as OrderPaid,
                    }
                default:
                    return null
            }
        },
        Dictionary.toList(queryParameters)
    )
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Data/SerializedOrderFilters.ts